import { useState } from 'react';

import { BaseButtonType } from '@displate/styleguide';
import clsx from 'clsx';

import { ToastNotification } from '@/common/components/Toast/ToastNotification/ToastNotification';
import { WishlistButtonStyled } from '@/common/components/WishlistButton/WishlistButton.styled';
import useToastContext from '@/common/contexts/ToastContext/useToastContext';
import { useAddToWishlist } from '@/common/hooks/useAddToWishlist/useAddToWishlist';
import { useHeaderContentInfo } from '@/common/hooks/useHeaderContent/useHeaderContentInfo';
import { useRemoveFromWishlist } from '@/common/hooks/useRemoveFromWishlist/useRemoveFromWishlist';
import { RouteToWishlistPage } from '@/modules/WishlistPage/WishlistPageConfig';

interface WishlistButtonProps {
  externalId: number;
  color?: 'white' | 'black';
  onAddToWishlist?: () => unknown;
  onRemoveFromWishlist?: () => unknown;
  className?: string;
}

enum WishlistAnimation {
  ADDING = 'ADDING',
  REMOVING = 'REMOVING',
  IDLE = 'IDLE',
}

export const WishlistButton = ({
  externalId,
  color = 'black',
  onAddToWishlist,
  onRemoveFromWishlist,
  className,
}: WishlistButtonProps) => {
  const { displayToast } = useToastContext();
  const optionsAdd = {
    onSuccess: () => {
      onAddToWishlist?.();
      displayToast({
        children: (
          <ToastNotification routeUrl={RouteToWishlistPage().route} text="Added to wishlist" />
        ),
      });
    },
  };
  const optionsRemove = { onSuccess: onRemoveFromWishlist };
  const { mutate: addToWishlist } = useAddToWishlist(optionsAdd);
  const { mutate: removeFromWishlist } = useRemoveFromWishlist(optionsRemove);
  const [wishlistAnimation, setWishlistAnimation] = useState<WishlistAnimation>(
    WishlistAnimation.IDLE,
  );
  const { data: headerContent, isLoading: isHeaderContentLoading } = useHeaderContentInfo();

  const isCurrentArtworkInWishlist = headerContent?.wishlistItemIds?.some(
    (wishlistItem) => wishlistItem === externalId,
  );

  const isInWishlist = isCurrentArtworkInWishlist && !isHeaderContentLoading;

  const handleWishlistClick = () => {
    if (isCurrentArtworkInWishlist) {
      setWishlistAnimation(WishlistAnimation.REMOVING);
      removeFromWishlist(externalId);
    } else {
      setWishlistAnimation(WishlistAnimation.ADDING);
      addToWishlist({ externalId });
    }

    setTimeout(() => {
      setWishlistAnimation(WishlistAnimation.IDLE);
    }, 2000);
  };

  return (
    <WishlistButtonStyled
      $color={color}
      aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      className={clsx(
        `${isInWishlist ? 'is-in-wishlist' : ''} ${
          WishlistAnimation.ADDING === wishlistAnimation ? 'is-adding-to-wishlist' : ''
        }`,
        className,
      )}
      data-testid="wishlist-icon-container"
      isAddingToWishlist={WishlistAnimation.ADDING === wishlistAnimation}
      isInWishlist={isInWishlist}
      isRemovingFromWishlist={WishlistAnimation.REMOVING === wishlistAnimation}
      onClick={handleWishlistClick}
      variant={BaseButtonType.PLAIN}
    />
  );
};
